import { useCallback, useMemo } from 'react'
import { useLocationContext } from '@gatsbyjs/reach-router'
import { useAuth } from '../context/AuthContext'
import { useAnalyticsContext } from './AnalyticsContext'
import {
  buildPageContext,
  buildUserContext,
  mergeAnalyticsPayload,
  pushAnalyticsEvent,
  readConsentState,
} from './dataLayer'

export const useAnalytics = (overrides = {}) => {
  const locationContext = useLocationContext()
  const location = locationContext?.location
  const { user } = useAuth()
  const ctx = useAnalyticsContext()

  const enabled = overrides.enabled ?? ctx.enabled ?? true

  const page = useMemo(
    () =>
      buildPageContext({
        location,
        ...(ctx.page || {}),
        ...(overrides.page || {}),
      }),
    [location, ctx.page, overrides.page]
  )

  const content = useMemo(
    () => ({ ...(ctx.content || {}), ...(overrides.content || {}) }),
    [ctx.content, overrides.content]
  )

  const component = useMemo(
    () => ({ ...(ctx.component || {}), ...(overrides.component || {}) }),
    [ctx.component, overrides.component]
  )

  const userContext = useMemo(() => buildUserContext(user), [user])

  const basePayload = useMemo(
    () => ({
      page,
      user: userContext,
      ...(Object.keys(content).length ? { content } : null),
      ...(Object.keys(component).length ? { component } : null),
    }),
    [page, userContext, content, component]
  )

  const track = useCallback(
    (event, payload = {}) => {
      if (!enabled) return
      const consent = readConsentState()
      const merged = mergeAnalyticsPayload(basePayload, {
        ...(consent ? { consent } : null),
        ...payload,
      })
      pushAnalyticsEvent(event, merged)
    },
    [enabled, basePayload]
  )

  return {
    track,
    page,
    user: userContext,
    content,
    component,
    enabled,
  }
}
